import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { AlertService } from '@core/ui-kit/services/alert.service';
import { catchError, filter, groupBy, map, mergeMap, of, switchMap, take, tap } from 'rxjs';
import { AdvancedBundleSummaryStatus } from '../enums/bundle-advanced-summary-status.enum';
import { AdvancedSummaryNotificationService } from '../service/advanced-summary-notification.service';
import { AdvancedSummaryService } from '../service/advanced-summary.service';
import { loadAdvancedSummaryActions } from './advanced-summary.actions';

@Injectable()
export class SummaryEffects {
  private readonly actions$ = inject(Actions);
  private readonly advancedSummaryService = inject(AdvancedSummaryService);
  private readonly advancedSummaryNotificationService = inject(AdvancedSummaryNotificationService);
  private readonly alertService = inject(AlertService);

  loadSummaryByID$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAdvancedSummaryActions.loadSummaryByID),
      mergeMap(({ id }) =>
        this.advancedSummaryService.getSummaryByBundleId(id).pipe(
          map((summary) => loadAdvancedSummaryActions.loadSummaryByIDSuccess({ summary })),
          catchError((error) => {
            if (error.status === 400 && error.error.message === 'NOT_FOUND') {
              return of(loadAdvancedSummaryActions.loadSummaryByIDSuccess({ summary: null }));
            }

            return of(
              loadAdvancedSummaryActions.loadSummaryByIDFailure({
                summaryError: error.error.message,
              }),
            );
          }),
        ),
      ),
    ),
  );

  pollSummaryByID$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAdvancedSummaryActions.loadSummaryByIDSuccess),
      filter(({ summary }) => summary?.status === AdvancedBundleSummaryStatus.GENERATING),
      groupBy(({ summary }) => summary!.bundleId),
      mergeMap((group$) =>
        group$.pipe(
          take(1),
          switchMap(({ summary }) =>
            this.advancedSummaryNotificationService.pollSummaryById(summary!.bundleId).pipe(
              map((finalSummary) => loadAdvancedSummaryActions.loadSummaryByIDSuccess({ summary: finalSummary })),
              catchError((error) => {
                if (error.status === 400 && error.error.message === 'NOT_FOUND') {
                  return of(loadAdvancedSummaryActions.loadSummaryByIDSuccess({ summary: null }));
                }
                return of(loadAdvancedSummaryActions.loadSummaryByIDFailure({ summaryError: error.error.message }));
              }),
            ),
          ),
        ),
      ),
    ),
  );

  uploadSummaryCSV$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAdvancedSummaryActions.uploadSummaryCSV),
      switchMap(({ id, file }) =>
        this.advancedSummaryService.uploadSummaryCsv(id, file).pipe(
          map(() =>
            loadAdvancedSummaryActions.loadSummaryByID({
              id,
            }),
          ),
        ),
      ),
    ),
  );

  validateFields$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAdvancedSummaryActions.validateSummaryFields),
      switchMap(({ id, fields }) =>
        this.advancedSummaryService.validateFields(id, fields).pipe(
          map(() =>
            loadAdvancedSummaryActions.loadSummaryByID({
              id,
            }),
          ),
        ),
      ),
    ),
  );

  invalidateFields$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAdvancedSummaryActions.invalidateSummaryFields),
      switchMap(({ id, fields }) =>
        this.advancedSummaryService.invalidateFields(id, fields).pipe(
          map(() =>
            loadAdvancedSummaryActions.loadSummaryByID({
              id,
            }),
          ),
        ),
      ),
    ),
  );

  deleteSummaryByID$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAdvancedSummaryActions.deleteSummaryByID),
      switchMap(({ id }) =>
        this.advancedSummaryService.deleteSummaryById(id).pipe(
          map(() =>
            loadAdvancedSummaryActions.loadSummaryByID({
              id,
            }),
          ),
        ),
      ),
    ),
  );

  editContentAnswer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAdvancedSummaryActions.editContentAnswer),
      switchMap(({ id, fieldId, manualAnswer }) =>
        this.advancedSummaryService.setUserAnswer(id, fieldId, manualAnswer).pipe(
          map(() =>
            loadAdvancedSummaryActions.loadSummaryByID({
              id,
            }),
          ),
        ),
      ),
    ),
  );

  generateSummaryByID$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAdvancedSummaryActions.generateSummaryByID),
      switchMap(({ id }) =>
        this.advancedSummaryService.generateSummaryById(id).pipe(
          map(() =>
            loadAdvancedSummaryActions.loadSummaryByID({
              id,
            }),
          ),
        ),
      ),
    ),
  );

  setDoNotUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAdvancedSummaryActions.setDoNotUpdate),
      switchMap(({ id, fields }) =>
        this.advancedSummaryService.setDoNotUpdate(id, fields).pipe(
          map(() =>
            loadAdvancedSummaryActions.loadSummaryByID({
              id,
            }),
          ),
        ),
      ),
    ),
  );

  sendToCrm$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadAdvancedSummaryActions.sendToCrm),
        switchMap(({ id }) =>
          this.advancedSummaryService.sendToCrm(id).pipe(tap(() => this.alertService.showOperationCompletedAlert())),
        ),
      ),
    { dispatch: false },
  );
}
